import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	reduxForm,
	Field,
	change,
	getFormValues
} from "redux-form";

import {
	Modal,
	Button,
	ReduxControls,
	Pager,
	ProgressIndicator,
	Search
} from "rhombus-react";

import {
	SEARCH_DEBOUNCE,
	SUPPRESS_SUCCESS_GROWL
} from "utils/constants";
import { useModalStack, noop } from "utils/common";
import { useCollectionFiltering } from "utils/DataGrid";
import { ImportVersions, fetchImportVersions } from "reducers/ImportVersions";
import { clearRefinements } from "reducers/Refinements";
import { fetchCreativesById } from "reducers/Creatives";
import ImportPreviewCreative from "./ImportPreviewCreative";
import ImportConfirmationModal from "./ImportConfirmationModal";
import EmptySection from "widgets/EmptySection";

import "./ImportPreviewBlade.css";

const IMPORT_VERSION_PREVIEW_FORM = "IMPORT_VERSION_PREVIEW_FORM";
const TWO_OPTIONS = 2;
const RPP = 25;
const ROW_COUNT_OPTIONS = [RPP];

function useJobData(jobId) {
	const dispatch = useDispatch();
	const [data, setData] = useState({});
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		dispatch({
			type: "GET_JOB_DATA",
			AJAX: {
				url: `jobs/${jobId}`,
				success: "GET_JOB_DATA_SUCCESS",
				error: "GET_JOB_DATA_ERROR",
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		}).then((response) => {
			setIsLoaded(true);

			if (response?.success) {
				setData(response?.payload?.data);
			} else {
				setHasError(true);
			}
		});
	}, []);

	return [data, isLoaded, hasError];
}

function JobNotFound() {
	const sectionProps = {
		className: "not-found",
		src: "/images/404.svg",
		header: "Import Expired",
		message: "Sorry.  Your Import Job may have expired.",
	};

	return <EmptySection {...sectionProps} />;
}

function ImportPreviewBlade(props) {
	const importVersions = useSelector(state => state.importVersions);
	const { isLoaded, items, totalPages, totalItems } = importVersions;
	const [creativeDetails, setCreativeDetails] = useState(null);
	const dispatch = useDispatch();
	const {
		onClose,
		jobId,
	} = props;
	const skipFirstFetch = useRef(true);

	const [jobData, isJobDataLoaded, hasJobDataError] = useJobData(jobId);
	const values = useSelector(getFormValues(IMPORT_VERSION_PREVIEW_FORM));
	const { creative } = values || {};
	// Array of creative ids to filter in this case only one at a time can be selected
	// as we have dropdown select - undefined if All creatives are selected.
	const creativeIds = creative?.value > 0 ? [creative?.value] : undefined;

	const { creatives = []} = jobData?.response?.changes || {};

	const [isFullScreen, setIsFullScreen] = useState(true);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);


	const libraryId = jobData?.creativeLibrary?.id;

	const {
		filterInfo: { page, rpp, searchTerm },
	} = useCollectionFiltering(
		importVersions,
		ImportVersions.setAdvancedFilter,
		() => {
			if (jobId && !isConfirmationModalOpen) {
				dispatch(fetchImportVersions(jobId, creativeIds));
			}
		}
	);

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: totalItems,
		showItemWord: "",
		rowCountItems: ROW_COUNT_OPTIONS,
		disabled: true, // Disable rpp change until virtual scrolling on imported preview is implemented
		onPageChange: newPage => dispatch(ImportVersions.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(ImportVersions.setRPP(newRPP)),
	};

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal, popModal }, ModalStackHead] = useModalStack({});

	// Retrigger fetch on creative selection change
	// And because it has custom parameter and we can not use advanced filter
	useEffect(() => {
		// Skip first pass to avoid double call of the endpoint
		// as useCollectionFiltering will trigger fetch on mount
		// Trigger fetch only if creative is selected
		if (creative) {
			if (!skipFirstFetch.current) {
				dispatch(fetchImportVersions(jobId, creativeIds));
			} else {
				skipFirstFetch.current = false;
			}
		}
	}, [creative]);

	useEffect(() => {
		if (!creatives.length) {
			return;
		}

		// Fetch creative details and create dictionary object by Ids
		const creativeIds = creatives.map(creative => creative.id);
		dispatch(fetchCreativesById(libraryId, creativeIds)).then((response) => {
			if (response?.success) {
				const creativeInfo = response?.payload?.data?.items || [];
				const mappedById = creativeInfo.reduce((prev, creative) => {
					return {
						...prev,
						[creative.id]: creative,
					};
				}, {});
				setCreativeDetails(mappedById);
			}
		});
	}, [creatives]);

	const creativeVersionsPreview = useMemo(() => {
		if (!creativeDetails) {
			return [];
		}
		// Break down paginated assets by creatives
		const creativeVersions = items.reduce((acc, item) => {
			const { creative, name, id, variableConfig } = item;
			const version = { name, id, variableConfig };
			const lastCreative = acc.at(-1);
			const lastCreativeId = lastCreative?.creative.id;

			if (lastCreativeId === creative.id) {
				lastCreative.versions.push(version);
			} else {
				acc.push({
					creative: creativeDetails[creative.id],
					versions: [version],
				});
			}

			return acc;
		}, []);

		// If length of versions is 0 when searchTerm is set
		// Let user know and give opportunity to reset search
		// When creativeVersions is empty on it's own this dialog will never be opened
		if (creativeVersions.length === 0 && searchTerm) {
			const emptyProps = {
				className: "empty-assets no-search-results",
				src: "/images/noSearch.svg",
				header: "No Search results to display",
				message: "Try adjusting your search to find what you are looking for",
				buttons: [
					<Button key="clear" onClick={() => dispatch(ImportVersions.setSearchTerm(""))}>Clear Search</Button>
				],
			};

			return <EmptySection {...emptyProps} />;
		}

		return creativeVersions.map(creativeVersion => (<ImportPreviewCreative key={creativeVersion.creative.id}
			libraryId={libraryId}
			creativeVersion={creativeVersion}
			jobId={jobId} />));
	}, [items, creativeDetails]);


	const creativeOptions = useMemo(() => {
		return [
			{ label: "All Creatives", value: 0 },
			...creatives
				.filter((creative) => {
					return creative.new + creative.updated;
				})
				.map((creative) => {
					return {
						label: creative.name,
						value: creative.id,
					};
				}),
		];
	}, [creatives]);

	const subtitle = (creativeOptions.length === TWO_OPTIONS) ? creativeOptions[1]?.label : `${creativeOptions.length - 1} Creatives`;
	const title = `Preview Version Import - ${subtitle}`;

	const onConfirmClose = () => {
		setIsConfirmationModalOpen(false);
		onClose();
	};

	const onPopModal = () => {
		setIsConfirmationModalOpen(false);
		popModal();
	};

	function openConfirm() {
		function Configured(props) {
			return (
				<ImportConfirmationModal
					jobId={jobId}
					libraryId={libraryId}
					creatives={creatives}
					onClose={onConfirmClose}
					onBack={onPopModal}
					{...props}
				/>
			);
		}
		pushModal(Configured);
		setIsConfirmationModalOpen(true);
	}

	useEffect(() => {
		const [firstCreative, secondCreative] = creativeOptions;
		// Set default option if not set
		// First option will always be 'All creatives' we only set it when number of options more than 2
		if (!creative) {
			if(creativeOptions.length === TWO_OPTIONS) {
				// If only two options we set creative to single available option and
				// selector is disabled
				if(secondCreative) {
					dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "creative", secondCreative));
				}
			} else {
				if(firstCreative) {
					dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "creative", firstCreative));
				}
			}
		}
	}, [creative, creativeOptions]);

	function closeModal() {
		dispatch(clearRefinements());
		dispatch(ImportVersions.reset());
		onClose();
	}

	function onToggleFullScreen() {
		setIsFullScreen(!isFullScreen);
	}

	function Content() {
		if (hasJobDataError) {
			return <JobNotFound />;
		}

		return (
			<div className="main-content-container">
				<ProgressIndicator isLoading={!isJobDataLoaded || !isLoaded} />
				<div className="version-selector">
					<Field
						name="creative"
						label="Creative"
						component={ReduxControls.Select}
						options={creativeOptions}
						className="creative-select left-label"
						isClearable={false}
						isDisabled={creativeOptions.length === TWO_OPTIONS}
						onMenuScrollToBottom={noop}
					/>
					<div className="versions-search">
						<Search
							searchDebounce={SEARCH_DEBOUNCE}
							onSearch={newSearchTerm => dispatch(ImportVersions.setSearchTerm(newSearchTerm))}
							searchValue={searchTerm}
							isLoading={!isLoaded}
							placeholder="Search Versions"
							shouldTrim={false}
						/>
					</div>
				</div>
				<div className="version-preview-container">{creativeVersionsPreview}</div>
				<Pager key="pager" {...pagerProps} />
			</div>
		);
	}

	function Footer() {
		return (
			<>
				<Button theme="default" onClick={closeModal}>Cancel</Button>
				<Button theme="green" disabled={hasJobDataError} onClick={openConfirm}>Import Versions</Button>
			</>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: "version-import-preview-blade",
		setAppTitle: false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter: Footer,
		renderContent: Content,

		onClose: closeModal,
	};

	return ModalStackHead ? <ModalStackHead /> : <Modal {...modalProps} />;
}

export default reduxForm({
	form: IMPORT_VERSION_PREVIEW_FORM,
})(ImportPreviewBlade);

ImportPreviewBlade.displayName = "ImportPreviewBlade";
